import {
  EnvironmentProviders,
  InjectionToken,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import { kdvWrap } from '@fido/firebase';
import { FirebaseApp } from '@fido/firebase/app';
import type { FirebasePerformance as FbPerformance } from 'firebase/performance';
import {
  getPerformance as rawGetPerformance,
  initializePerformance as rawInitializePerformance,
  trace as rawTrace,
} from 'firebase/performance';
import {
  traceUntil as vendorTraceUntil,
  traceUntilComplete as vendorTraceUntilComplete,
  traceUntilFirst as vendorTraceUntilFirst,
  traceWhile as vendorTraceWhile,
} from './lib/vendor';

// 1) Wrapped firebase/performance functions. Mirrors @angular/fire's
//    `_zoneWrap(fn, blockUntilFirst)` declarations from
//    angular-fire-performance.mjs.
export const getPerformance = kdvWrap(rawGetPerformance, true);
export const initializePerformance = kdvWrap(rawInitializePerformance, true);
export const trace = kdvWrap(rawTrace, true);
export type { PerformanceTrace } from 'firebase/performance';

// 2) DI token (declaration-merged with type alias).
export type Performance = FbPerformance;
export const Performance = new InjectionToken<FbPerformance>('@fido/firebase.performance');

export function providePerformance(factory: () => FbPerformance): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Performance,
      // FirebaseApp dep forces initializeApp() to run before this factory —
      // mirrors @angular/fire's DEFAULT_PERFORMANCE_INSTANCE_PROVIDER.
      useFactory: (_app: unknown) => factory(),
      deps: [FirebaseApp],
    },
    // Eagerly initialize so the SDK starts capturing measurements (matches
    // the pattern in fido's existing providers.ts).
    provideEnvironmentInitializer(() => inject(Performance)),
  ]);
}

// 3) Vendored rxfire helpers — Observable-shaped trace operators. Each is
//    wrapped in kdvWrap so subscriptions hold a PendingTask until first
//    emission. Identical surface to @angular/fire/performance.
//    Note: rxfire's `trace` operator is intentionally NOT exported here.
//    @angular/fire shadows it with the firebase/performance `trace` function
//    (which creates a PerformanceTrace instance) — we mirror that shadowing.
export const traceUntil = kdvWrap(vendorTraceUntil, true);
export const traceUntilComplete = kdvWrap(vendorTraceUntilComplete, true);
export const traceUntilFirst = kdvWrap(vendorTraceUntilFirst, true);
export const traceWhile = kdvWrap(vendorTraceWhile, true);
